import { DataTablePageSize } from '@gamma/ui/data-table';
import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { IdType, SortingRule } from 'react-table';
import {
  AlertMetadataPaginated,
  AlertSummary,
  ExcludedEntity,
  PaginatedAlerts,
  PaginatedAlertSummaries,
  PaginatedDetections,
  PaginatedDetectionSummaries,
  PaginatedExcludedEntities,
} from '@investigator/features/detections/queries';
import {
  AlertMetadata,
  Detection,
  DetectionSummary,
  UserHistory as GQLUserHistory,
  PaginatedNicHistory as GQLPaginatedNicHistory,
  PaginatedUserHistory as GQLPaginatedUserHistory,
} from '@investigator/shared/generated/graphql';
import {
  NICHistory,
  PaginatedNICHistory,
  UserHistory,
} from '@investigator/features/entities/queries';
import { GetUserLogsQuery } from '../../generated/graphql';
import { DEFAULT_TABLE_PAGE_SIZE } from '../../constants';
import {
  EntraSigninWithUser,
  GetUserSigninHistoryQuery,
} from '@investigator/shared/generated/graphql';

export type InvestigatorTableType =
  | AlertSummary
  | AlertMetadata
  | ExcludedEntity
  | Detection
  | DetectionSummary
  | NICHistory
  | UserHistory
  | EntraSigninWithUser
  | GQLUserHistory;

export type PageCountItems =
  | PaginatedAlerts
  | PaginatedAlertSummaries
  | AlertMetadataPaginated
  | PaginatedExcludedEntities
  | PaginatedDetections
  | PaginatedDetectionSummaries
  | PaginatedNICHistory
  | GetUserLogsQuery['getUserLogs']
  | GetUserSigninHistoryQuery['getUserSigninHistory']
  | GQLPaginatedNicHistory
  | GQLPaginatedUserHistory;

export const useManualPagination = <TableType extends InvestigatorTableType>({
  initialFetch,
  sortBy: initialSortBy = [],
  offsetReset = false,
  setOffsetReset,
  initPageSize = DEFAULT_TABLE_PAGE_SIZE,
}: {
  initialFetch?: boolean;
  sortBy?: SortingRule<TableType>[];
  offsetReset?: boolean;
  setOffsetReset?: Dispatch<SetStateAction<boolean>>;
  pageIndexParam?: number;
  initPageSize?: DataTablePageSize | null;
} = {}): {
  sortBy: SortingRule<TableType>[] | [];
  offset: number;
  pageSize: DataTablePageSize;
  pageIndex: number;
  pageCount: number;
  onFetchData: (
    pageIndex: number,
    pageSize: number,
    sortBy: SortingRule<TableType>[],
  ) => void;
  getPageCount: (items?: PageCountItems) => void;
  getSortsForAPI: (sortBy: SortingRule<TableType>[]) => {
    sort_by: IdType<TableType>;
    sort_dir: string;
  }[];
  formatNestedSorts: (sortBy: SortingRule<TableType>[]) => {
    id: IdType<TableType>;
    desc: boolean | undefined;
  }[];
  resetPagination: () => void;
} => {
  const prevInitialFetch = useRef(initialFetch);

  const [offset, setOffset] = useState<number>(0);
  const [pageCount, setPageCount] = useState<number>(1);
  const [pageIndex, setPageIndex] = useState<number>(0);
  const [pageSize, setPageSize] = useState<DataTablePageSize>(
    initPageSize ?? DEFAULT_TABLE_PAGE_SIZE,
  );
  const [sortBy, setSortBy] = useState<SortingRule<TableType>[]>(initialSortBy);

  const getPageCount = (items?: PageCountItems) => {
    const pages = Math.ceil((items?.total_items ?? 0) / (items?.size ?? 1));
    setPageCount(pages);
    return;
  };

  const formatNestedSorts = (sortBy: SortingRule<TableType>[]) =>
    sortBy.map((sort) => {
      return {
        id:
          sort.id === 'timestamp'
            ? 'alert_timestamp.observed'
            : sort.id === 'entity_name'
              ? 'entity_name.keyword'
              : sort.id === 'created_by_alias'
                ? 'created_by_alias.keyword'
                : sort.id,
        desc: sort.desc,
      };
    });

  const getSortsForAPI = (sortBy: SortingRule<TableType>[]) => {
    const sorts = sortBy.map((sort: SortingRule<TableType>) => {
      return {
        sort_by: sort.id,
        sort_dir: sort.desc === true ? 'desc' : 'asc',
      };
    });
    return sorts;
  };

  const onFetchData = useMemo(
    () =>
      (
        pageIndex: number,
        pageSize: number,
        sortBy: SortingRule<TableType>[],
      ) => {
        setOffset(pageIndex * pageSize);
        setSortBy(sortBy);
        setPageIndex(offsetReset ? 0 : pageIndex);
        setOffset(pageIndex * pageSize);
        setPageSize(pageSize as DataTablePageSize);
        setOffsetReset?.(false);
      },
    [offsetReset, setOffsetReset],
  );

  useEffect(() => {
    if (pageIndex >= pageCount && pageCount > 1) {
      setOffsetReset?.(true);
      setPageIndex(0);
      setOffset(0);
    }
  }, [setOffsetReset, pageIndex, pageCount]);

  useEffect(() => {
    if (prevInitialFetch.current !== initialFetch) {
      prevInitialFetch.current = initialFetch;
      setOffset(0);
      setPageIndex(0);
      // Reset to the configured page size, not hardcoded value
      setPageSize(initPageSize ?? DEFAULT_TABLE_PAGE_SIZE);
      // Reset sortBy to initial value
      setSortBy(initialSortBy);
    }
  }, [initialFetch, initPageSize, initialSortBy]);

  const resetPagination = () => {
    setOffsetReset?.(true);
    setPageIndex(0);
    setOffset(0);
  };

  return {
    sortBy,
    offset,
    pageSize,
    pageIndex,
    pageCount,
    onFetchData,
    getPageCount,
    getSortsForAPI,
    formatNestedSorts,
    resetPagination,
  };
};
